// React / Next
import { useState, useCallback } from 'react';

// Types
type HubSpotFormConfig = {
    portalId: string;
    formId: string;
    region?: string;
};

type HubSpotField = {
    objectTypeId?: string;
    name: string;
    value: string;
};

type HubSpotSubmissionData = {
    fields: HubSpotField[];
    context?: {
        pageUri?: string;
        pageName?: string;
    };
};

type UseHubSpotFormReturn = {
    submit: (fields: HubSpotField[]) => Promise<boolean>;
    isSubmitting: boolean;
    isSuccess: boolean;
    error: string | null;
    reset: () => void;
};

/**
 * Custom hook for submitting forms to HubSpot Forms API
 * Allows using custom UI components while sending data to HubSpot
 *
 * @param config - HubSpot form configuration (portalId, formId, region)
 * @returns Object with submit function and state (isSubmitting, isSuccess, error)
 *
 * @example
 * const { submit, isSubmitting, isSuccess, error } = useHubSpotForm({
 *   portalId: '47835668',
 *   formId: '79a8a1bc-25c5-4964-871e-17848e54b7d3',
 *   region: 'na1'
 * });
 *
 * const handleSubmit = async (email: string) => {
 *   await submit([{ name: 'email', value: email }]);
 * };
 */
export const useHubSpotForm = (config: HubSpotFormConfig): UseHubSpotFormReturn => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { portalId, formId, region = 'na1' } = config;

    // Build the API endpoint URL based on region
    const getApiUrl = useCallback(() => {
        const baseUrl =
            region === 'eu1'
                ? 'https://api-eu1.hsforms.com'
                : 'https://api.hsforms.com';
        return `${baseUrl}/submissions/v3/integration/submit/${portalId}/${formId}`;
    }, [portalId, formId, region]);

    const submit = useCallback(
        async (fields: HubSpotField[]): Promise<boolean> => {
            setIsSubmitting(true);
            setError(null);
            setIsSuccess(false);

            const data: HubSpotSubmissionData = {
                fields,
                context: {
                    pageUri: typeof window !== 'undefined' ? window.location.href : '',
                    pageName: typeof document !== 'undefined' ? document.title : '',
                },
            };

            try {
                const response = await fetch(getApiUrl(), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(data),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage =
                        errorData.message ||
                        errorData.errors?.[0]?.message ||
                        'Failed to submit form. Please try again.';
                    throw new Error(errorMessage);
                }

                setIsSuccess(true);
                return true;
            } catch (err) {
                const errorMessage =
                    err instanceof Error ? err.message : 'An unexpected error occurred';
                setError(errorMessage);
                return false;
            } finally {
                setIsSubmitting(false);
            }
        },
        [getApiUrl]
    );

    const reset = useCallback(() => {
        setIsSubmitting(false);
        setIsSuccess(false);
        setError(null);
    }, []);

    return {
        submit,
        isSubmitting,
        isSuccess,
        error,
        reset,
    };
};

// Newsletter form configuration
export const HUBSPOT_NEWSLETTER_CONFIG: HubSpotFormConfig = {
    portalId: import.meta.env.VITE_HUBSPOT_PORTAL_ID || '',
    formId: import.meta.env.VITE_HUBSPOT_NEWSLETTER_FORM_ID || '',
    region: import.meta.env.VITE_HUBSPOT_REGION || 'na1',
};

// Request a Demo form configuration
export const HUBSPOT_REQUEST_DEMO_CONFIG: HubSpotFormConfig = {
    portalId: import.meta.env.VITE_HUBSPOT_PORTAL_ID || '',
    formId: import.meta.env.VITE_HUBSPOT_REQUEST_DEMO_FORM_ID || '',
    region: import.meta.env.VITE_HUBSPOT_REGION || 'na1',
};

// Partner form configuration
export const HUBSPOT_PARTNER_CONFIG: HubSpotFormConfig = {
    portalId: '47835668',
    formId: 'e7333a65-e846-4cc1-a33e-310cf57b4633',
    region: 'na1',
};
